// GRP Inspection — rebuilt in the Service page's shape so it reads the same way across the app.
//   • Board — one card per boat, with a green/total inspection roll-up, its hull-colour swatch and a
//     "needs attention" tint; open a card to see the boat's full inspection outline (RYG balls,
//     colour swatches, tags) with every cell editable.
//   • Sheet — the faithful full grid: the whole Smartsheet outline (collapsible boats/sections/points)
//     in one scrollable table.
// A KPI strip, a search box, RYG status filters, a "needs attention" toggle, the boat's model (the MODEL
// column) and the month of a date sit above both. Editors
// (grp:write) change any cell, add or remove tags, and add or delete rows; changes save straight back
// to Smartsheet. Read-only for everyone else.
import { useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import {
  useGrpGrid, useGrpGridWrite, useGrpAddRow, useGrpDeleteRow,
  type SheetGridColumn, type SheetGridRow,
} from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { Chip, ChipGroup, EmptyState, ErrorState, FilterBar, FilterSelect, FilterToggle, InlineError, LoadingState, PageHeader, Pill, SearchInput, Segmented, SyncLabel, useConfirm } from '../components/ui.js';
import { IconCheck, IconChevronRight, IconCopy, IconTrash } from '../components/icons.js';
import { useCellHistoryHover } from '../components/CellHistory.js';
import { NameField } from '../components/NameField.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { sortRows, useTableSort } from '../lib/tableSort.js';
import { useTabState } from '../lib/tabState.js';
import { useDateFormatter } from '../lib/format.js';
import { SortMark } from '../components/SortMark.js';
import { MultiPick } from '../components/MultiPick.js';
import { GrpAddBoat } from './GrpAddBoat.js';
import { GrpApplyTasks } from './GrpApplyTasks.js';

// Smartsheet RYG-symbol option names → a status dot colour.
const DOT: Record<string, string> = {
  green: '#2f9e44', yellow: '#f0a500', red: '#e03131', blue: '#1c7ed6',
  gray: '#adb5bd', grey: '#adb5bd',
};
// order the status filter chips sensibly (attention colours first).
const STATUS_ORDER = ['red', 'yellow', 'blue', 'green', 'grey', 'gray'];
// the Model filter's value for boats without a model
const NO_MODEL = '(none)';
// rows drawn at a time (Sheet), and opened by a search (Board): a phone holds far fewer live rows than a desktop
const ROW_PAGE = 1500;
const ROW_PAGE_TOUCH = 200;
// common hull / finish colour words → a swatch fill.
const SWATCH: Record<string, string> = {
  white: '#f2f2f2', black: '#1c1c1c', red: '#d0342c', blue: '#2b5fb0', navy: '#1b2a4a',
  grey: '#8b93a0', gray: '#8b93a0', silver: '#c8ccd2', green: '#2f7d4f', gold: '#c9a227',
  orange: '#e8720c', yellow: '#e9c40a', beige: '#e4d5b7', teal: '#177e89', bronze: '#8c6a3f',
};

const cellStr = (v: string | boolean | undefined): string =>
  v === true ? '✓' : v === false ? '' : v == null ? '' : String(v);

const stop = (e: MouseEvent) => e.stopPropagation();

export function Grp() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useGrpGrid();
  const { can } = useAuth();
  const canWrite = can('grp:write');
  const df = useDateFormatter();
  const write = useGrpGridWrite();
  const addRow = useGrpAddRow();
  const delRow = useGrpDeleteRow();
  const isPhone = useIsPhone();
  const coarse = useIsCoarse();

  // the view and filters stay as you left them when you come back to the page (this tab only)
  const [q, setQ] = useTabState('grp.q', '');
  const [view, setView] = useTabState<'board' | 'sheet'>('grp.view', 'sheet'); // Sheet is GRP's main view
  const [statusSel, setStatusSel] = useTabState('grp.status', 'ALL'); // 'ALL' or a lowercased RYG value
  const [onlyOpen, setOnlyOpen] = useTabState('grp.open', false);    // boats with any non-green inspection point
  const [showEmpty, setShowEmpty] = useState(false);
  const [dateColId, setDateColId] = useState<number | null>(null); // which date the Month filter reads
  const [monthSel, setMonthSel] = useTabState('grp.month', 'ALL');    // 'ALL' or 'YYYY-MM'
  const [modelSel, setModelSel] = useTabState('grp.model', 'ALL');    // 'ALL', a model's key, or NO_MODEL
  // sort the boats by any column (a header click in Sheet, the Sort select in Board): ascending,
  // descending, then back to the sheet's order. Child rows always stay under their boat.
  const { sort: storedSort, setSort, dirOf, th: sortTh } = useTableSort<number>('grp');
  const [openBoats, setOpenBoats] = useState<Set<number>>(new Set()); // board: which cards are open
  // sheet: the outline rows opened. Every parent starts collapsed, so the first draw is just the boat
  // list: the sheet holds thousands of rows (each boat's QA tasks), and drawing them all at once, even for
  // a moment, crashed the page on phones.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [everExpanded, setEverExpanded] = useState(false);
  const [editing, setEditing] = useState<{ rowId: number; colId: number } | null>(null);
  const [adding, setAdding] = useState(false); // the "+ Boat" form
  const [justAdded, setJustAdded] = useState<number | null>(null); // a boat just added: opened and scrolled to
  const [draft, setDraft] = useState('');

  const { onEnter: histEnter, onLeave: histLeave, bindPress: histPress, historyButton: histButton, popover: histPopover } = useCellHistoryHover(data?.sheetId);
  const [confirmNode, confirm] = useConfirm();

  const columns = data?.columns ?? [];
  const allRows = data?.rows ?? [];
  const primaryId = useMemo(() => (columns.find((c) => c.primary) ?? columns[0])?.id, [columns]);
  const rowName = (r: SheetGridRow) => cellStr(r.cells[primaryId ?? -1]);
  const symbolCols = useMemo(() => columns.filter((c) => !!c.symbol), [columns]);
  // the boat's SAP number, shown on its Board card ("SAP", "SAP No", not "SAP_Model")
  const sapCol = useMemo(() => columns.find((c) => /^sap\b/i.test(c.title.trim())), [columns]);
  const modelCol = useMemo(() => columns.find((c) => /^model\b/i.test(c.title.trim())), [columns]);
  const symbolIds = useMemo(() => new Set(symbolCols.map((c) => c.id)), [symbolCols]);
  // a sort kept from before only counts while its column is still on the sheet
  const sort = storedSort && columns.some((c) => c.id === storedSort.key) ? storedSort : null;

  // parent → children, depth, and which rows have children (for the outline toggles).
  const { childrenOf, depthOf, hasKids, topLevel } = useMemo(() => {
    const byId = new Map(allRows.map((r) => [r.id, r]));
    const kids = new Map<number, SheetGridRow[]>();
    const top: SheetGridRow[] = [];
    for (const r of allRows) {
      if (r.parentId != null && byId.has(r.parentId)) {
        const arr = kids.get(r.parentId);
        if (arr) arr.push(r);
        else kids.set(r.parentId, [r]);
      } else {
        top.push(r);
      }
    }
    const depthCache = new Map<number, number>();
    const depthOf = (r: SheetGridRow): number => {
      if (depthCache.has(r.id)) return depthCache.get(r.id)!;
      let d = 0;
      let cur: SheetGridRow | undefined = r;
      while (cur && cur.parentId != null) { const p = byId.get(cur.parentId); if (!p) break; d++; cur = p; }
      depthCache.set(r.id, d);
      return d;
    };
    return { childrenOf: (id: number) => kids.get(id) ?? [], depthOf, hasKids: (id: number) => kids.has(id), topLevel: top };
  }, [allRows]);

  // columns blank on EVERY row — hidden by default so the sheet's empty trailing columns don't force
  // endless horizontal scrolling. The primary column is always kept.
  const emptyColIds = useMemo(() => {
    const nonEmpty = new Set<number>();
    for (const r of allRows) {
      for (const c of columns) {
        if (nonEmpty.has(c.id)) continue;
        const v = r.cells[c.id];
        if (c.type === 'CHECKBOX' ? v === true : cellStr(v) !== '') nonEmpty.add(c.id);
      }
    }
    return new Set(columns.filter((c) => c.id !== primaryId && !nonEmpty.has(c.id)).map((c) => c.id));
  }, [allRows, columns, primaryId]);

  const cols = useMemo(
    () => (showEmpty ? columns : columns.filter((c) => !emptyColIds.has(c.id))),
    [columns, emptyColIds, showEmpty],
  );
  const valueCols = useMemo(() => cols.filter((c) => c.id !== primaryId), [cols, primaryId]);
  // what the boats can be sorted by: the vessel column, then every column the boats themselves fill
  const sortOptions = useMemo(() => {
    const filled = columns.filter((c) => c.id === primaryId || topLevel.some((b) => {
      const v = b.cells[c.id];
      return c.type === 'CHECKBOX' ? v === true : cellStr(v).trim() !== '';
    }));
    return [...filled.filter((c) => c.id === primaryId), ...filled.filter((c) => c.id !== primaryId)]
      .map((c) => ({ value: String(c.id), label: c.title }));
  }, [columns, primaryId, topLevel]);
  // Sheet on phones: the status (symbol) columns follow the pinned name, so a row's dot is on screen
  // without a sideways swipe. Desktop keeps the Smartsheet order.
  const sheetCols = useMemo(
    () => (isPhone ? [...cols.filter((c) => c.id === primaryId), ...cols.filter((c) => c.id !== primaryId && !!c.symbol), ...cols.filter((c) => c.id !== primaryId && !c.symbol)] : cols),
    [cols, primaryId, isPhone],
  );

  // date columns a boat actually fills — these drive the Month filter and the ascending/descending sort.
  const dateCols = useMemo(
    () => columns.filter((c) => (c.type === 'DATE' || c.type === 'DATETIME') && topLevel.some((b) => cellStr(b.cells[c.id]).trim() !== '')),
    [columns, topLevel],
  );
  // default the date to a delivery-ish column (else completion/finish, else the first), kept valid as data loads.
  const defaultDateId = useMemo(
    () => (dateCols.find((c) => /deliver/i.test(c.title)) ?? dateCols.find((c) => /complet|finish/i.test(c.title)) ?? dateCols[0])?.id,
    [dateCols],
  );
  useEffect(() => {
    if (defaultDateId == null) { if (dateColId !== null) setDateColId(null); return; }
    if (dateColId != null && dateCols.some((c) => c.id === dateColId)) return;
    setDateColId(defaultDateId);
  }, [dateCols, dateColId, defaultDateId]);
  const boatDateIso = (b: SheetGridRow) => (dateColId != null ? cellStr(b.cells[dateColId]).trim() : '');
  // the YYYY-MM buckets present on the chosen date column, newest first for the dropdown.
  const months = useMemo(() => {
    const set = new Set<string>();
    for (const b of topLevel) { const d = boatDateIso(b); if (/^\d{4}-\d{2}/.test(d)) set.add(d.slice(0, 7)); }
    return [...set].sort().reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevel, dateColId]);
  // a month that no longer exists on the chosen date column (column switch, or a Sync) falls back to all,
  // otherwise the select reads "All months" while the stale filter keeps hiding boats. Not before the date
  // column is chosen and the boats are in (on the way back to the page), or the kept month is lost.
  useEffect(() => {
    if (dateColId != null && topLevel.length && monthSel !== 'ALL' && !months.includes(monthSel)) setMonthSel('ALL');
  }, [months, monthSel, dateColId, topLevel.length]);
  const monthLabel = (ym: string) => df.month(ym);

  // the boats' models (the MODEL column, as the sheet writes it: "GTx" and "GTX" are one model), in the
  // column's own dropdown order, with how many boats each; boats without one are "No model".
  const boatModelKey = (b: SheetGridRow) => (modelCol ? cellStr(b.cells[modelCol.id]).trim().toLowerCase() : '') || NO_MODEL;
  const models = useMemo(() => {
    if (!modelCol) return [];
    const found = new Map<string, { label: string; n: number }>();
    for (const b of topLevel) {
      const key = boatModelKey(b);
      const m = found.get(key) ?? { label: key === NO_MODEL ? 'No model' : cellStr(b.cells[modelCol.id]).trim(), n: 0 };
      m.n++;
      found.set(key, m);
    }
    const order = modelCol.options.map((o) => o.trim().toLowerCase());
    const rank = (k: string) => (k === NO_MODEL ? 1e6 : order.indexOf(k) < 0 ? 1e5 : order.indexOf(k));
    return [...found].sort(([a, x], [b, y]) => rank(a) - rank(b) || x.label.localeCompare(y.label)).map(([key, m]) => ({ key, ...m }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevel, modelCol]);
  // a model no boat has any more (a Sync, or another sheet) falls back to all, as the month does
  useEffect(() => { if (modelSel !== 'ALL' && models.length && !models.some((m) => m.key === modelSel)) setModelSel('ALL'); }, [models, modelSel]);

  // descendant count + a RYG roll-up (green vs total inspection points) for the collapsed views.
  const { countDesc, rollup } = useMemo(() => {
    const cCache = new Map<number, number>();
    const countDesc = (id: number): number => {
      if (cCache.has(id)) return cCache.get(id)!;
      let n = 0;
      for (const ch of childrenOf(id)) n += 1 + countDesc(ch.id);
      cCache.set(id, n);
      return n;
    };
    const rCache = new Map<number, { green: number; total: number }>();
    const rollup = (id: number): { green: number; total: number } => {
      if (rCache.has(id)) return rCache.get(id)!;
      let green = 0;
      let total = 0;
      for (const ch of childrenOf(id)) {
        for (const sc of symbolCols) {
          const val = String(ch.cells[sc.id] ?? '').trim().toLowerCase();
          if (val) { total += 1; if (val === 'green') green += 1; }
        }
        const sub = rollup(ch.id);
        green += sub.green; total += sub.total;
      }
      const res = { green, total };
      rCache.set(id, res);
      return res;
    };
    return { countDesc, rollup };
  }, [childrenOf, symbolCols]);

  // per-boat inspection summary over the WHOLE subtree (the boat row's own symbol cells + every
  // descendant): green/total for the roll-up badge, and the set of RYG values present (for filtering).
  const boatInfo = useMemo(() => {
    const info = new Map<number, { green: number; total: number; statuses: Set<string> }>();
    const symIds = symbolCols.map((c) => c.id);
    const collect = (root: SheetGridRow) => {
      const acc = { green: 0, total: 0, statuses: new Set<string>() };
      const walk = (r: SheetGridRow) => {
        for (const id of symIds) {
          const v = String(r.cells[id] ?? '').trim().toLowerCase();
          if (v) { acc.total++; acc.statuses.add(v); if (v === 'green') acc.green++; }
        }
        for (const ch of childrenOf(r.id)) walk(ch);
      };
      walk(root);
      return acc;
    };
    for (const b of topLevel) info.set(b.id, collect(b));
    return info;
  }, [topLevel, childrenOf, symbolCols]);

  const allStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const info of boatInfo.values()) for (const s of info.statuses) set.add(s);
    return [...set].sort((a, b) => {
      const ia = STATUS_ORDER.indexOf(a), ib = STATUS_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
  }, [boatInfo]);

  // accents and case don't matter: "νικος" finds Νίκος; ticks never match text
  const query = fold(q.trim());

  // rows matching the search, plus their ancestors so hits stay in context.
  const searchKeep = useMemo(() => {
    if (!query) return null;
    const byId = new Map(allRows.map((r) => [r.id, r]));
    const keep = new Set<number>();
    for (const r of allRows) {
      if (!Object.values(r.cells).some((c) => hasText(query, c))) continue;
      let cur: SheetGridRow | undefined = r;
      while (cur) { keep.add(cur.id); cur = cur.parentId != null ? byId.get(cur.parentId) : undefined; }
    }
    return keep;
  }, [allRows, query]);

  // the boats passing search + status + attention filters — the roots for both views.
  const visibleBoats = useMemo(() => {
    const list = topLevel.filter((b) => {
      if (searchKeep && !searchKeep.has(b.id)) return false;
      const info = boatInfo.get(b.id);
      if (statusSel !== 'ALL' && !(info?.statuses.has(statusSel))) return false;
      if (onlyOpen && !(info && info.total > 0 && info.green < info.total)) return false;
      if (monthSel !== 'ALL' && boatDateIso(b).slice(0, 7) !== monthSel) return false;
      if (modelSel !== 'ALL' && boatModelKey(b) !== modelSel) return false;
      return true;
    });
    // a status (symbol) column sorts by attention: red, yellow, blue, green; blanks always last
    return sortRows(list, sort, (b, key) => {
      const v = b.cells[key];
      if (!symbolIds.has(key)) return v;
      const i = STATUS_ORDER.indexOf(cellStr(v).trim().toLowerCase());
      return cellStr(v).trim() ? (i < 0 ? STATUS_ORDER.length : i) : null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevel, searchKeep, boatInfo, statusSel, onlyOpen, monthSel, dateColId, modelSel, modelCol, sort, symbolIds]);

  const kpis = useMemo(() => {
    let allGreen = 0, green = 0, total = 0;
    for (const b of visibleBoats) {
      const i = boatInfo.get(b.id);
      if (!i) continue;
      green += i.green; total += i.total;
      if (i.total > 0 && i.green === i.total) allGreen++;
    }
    return { boats: visibleBoats.length, allGreen, green, total };
  }, [visibleBoats, boatInfo]);

  // sheet: the visible, ordered row list — walk the tree top-down from the visible boats, honouring
  // collapse (or the search).
  const visible = useMemo(() => {
    const out: SheetGridRow[] = [];
    const walk = (rows: SheetGridRow[]) => {
      for (const r of rows) {
        if (searchKeep && !searchKeep.has(r.id)) continue;
        out.push(r);
        if (hasKids(r.id) && (searchKeep || expanded.has(r.id))) walk(childrenOf(r.id));
      }
    };
    walk(visibleBoats);
    return out;
  }, [visibleBoats, childrenOf, hasKids, expanded, searchKeep]);

  // the sheet is drawn a page of rows at a time (Show more below it): an Expand all, or a search that
  // opens many boats, would otherwise put thousands of live rows on a phone at once
  const rowPage = isPhone || coarse ? ROW_PAGE_TOUCH : ROW_PAGE;
  const [rowLimit, setRowLimit] = useState(rowPage);
  useEffect(() => { setRowLimit(rowPage); }, [rowPage, query, statusSel, onlyOpen, monthSel, view, sort]);
  const shownRows = visible.length > rowLimit ? visible.slice(0, rowLimit) : visible;

  // board: the descendant rows of a boat, pre-order, respecting the search (cards expand fully).
  const boatDescendants = (boatId: number): Array<{ r: SheetGridRow; depth: number }> => {
    const out: Array<{ r: SheetGridRow; depth: number }> = [];
    const walk = (pid: number, depth: number) => {
      for (const r of childrenOf(pid)) {
        if (searchKeep && !searchKeep.has(r.id)) continue;
        out.push({ r, depth });
        walk(r.id, depth + 1);
      }
    };
    walk(boatId, 0);
    return out;
  };

  const toggle = (id: number) =>
    setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const expand = (id: number) => setExpanded((s) => (s.has(id) ? s : new Set(s).add(id)));
  const toggleBoat = (id: number) =>
    setOpenBoats((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // while searching, the Board opens the boats with hits by itself, as many as fit in a page of rows (the
  // rest stay closed with their hits counted; a tap opens one)
  const searchOpen = useMemo(() => {
    if (!searchKeep) return null;
    const open = new Set<number>();
    let room = rowPage;
    for (const b of visibleBoats) {
      if (room <= 0) break;
      let n = 0;
      const walk = (pid: number) => { for (const r of childrenOf(pid)) if (searchKeep.has(r.id)) { n++; walk(r.id); } };
      walk(b.id);
      open.add(b.id);
      room -= n;
    }
    return open;
  }, [searchKeep, visibleBoats, childrenOf, rowPage]);
  const isBoatOpen = (id: number) => openBoats.has(id) || !!searchOpen?.has(id);

  const collapseAll = () => { setExpanded(new Set()); setEverExpanded(false); };
  const expandAll = () => {
    const n = new Set<number>();
    for (const r of allRows) if (hasKids(r.id)) n.add(r.id);
    setExpanded(n);
    setEverExpanded(true);
  };

  const isSymbolCol = (c: SheetGridColumn) => !!c.symbol;
  const isColorCol = (c: SheetGridColumn) => /colou?r/i.test(c.title);
  const editingHere = (r: SheetGridRow, c: SheetGridColumn) => editing?.rowId === r.id && editing?.colId === c.id;

  const writeCell = (rowId: number, colId: number, value: string | boolean | string[] | null) =>
    write.mutate({ rowId, columnId: colId, value });
  const beginEdit = (r: SheetGridRow, c: SheetGridColumn, e: { stopPropagation(): void }) => {
    e.stopPropagation();
    setEditing({ rowId: r.id, colId: c.id });
    setDraft(cellStr(r.cells[c.id]));
  };
  // keyboard path into the same editor (Enter / Space on a focused cell)
  const editKey = (r: SheetGridRow, c: SheetGridColumn) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); beginEdit(r, c, e); }
  };
  const commitText = (r: SheetGridRow, c: SheetGridColumn) => {
    if (draft !== cellStr(r.cells[c.id])) writeCell(r.id, c.id, draft === '' ? null : draft);
    setEditing(null);
  };

  // "+ Boat" opens the form (GrpAddBoat): name, SAP, model, and the QA task rows copied from a boat of the
  // same kind. Once added, the new boat opens and comes into view.
  const addBoat = () => { if (primaryId != null) setAdding(true); };
  // "Apply QA rows" (GrpApplyTasks): a boat already on the sheet takes the QA rows it's missing from another
  // boat. From the header (pick the boat) or a boat's own button; the boat opens and comes into view after.
  const [applying, setApplying] = useState<{ boatId: number | null } | null>(null);
  const applyTo = (boatId: number | null) => { if (primaryId != null) setApplying({ boatId }); };
  const onBoatAdded = (id: number) => {
    expand(id);
    setOpenBoats((s) => new Set(s).add(id));
    setJustAdded(id);
  };
  useEffect(() => {
    if (justAdded == null || !allRows.some((r) => r.id === justAdded)) return;
    // a boat added past the rows drawn so far: draw down to it first
    const at = visible.findIndex((r) => r.id === justAdded);
    if (at >= rowLimit) { setRowLimit(at + rowPage); return; }
    const el = document.querySelector(`[data-row-id="${justAdded}"]`);
    el?.scrollIntoView({ block: 'center' });
    setJustAdded(null);
  }, [justAdded, allRows, visible, rowLimit, rowPage]);
  const addChild = (r: SheetGridRow) => {
    if (primaryId == null) return;
    addRow.mutate({ cells: [{ columnId: primaryId, value: '' }], parentId: r.id });
    expand(r.id);
    setOpenBoats((s) => new Set(s).add(r.id));
  };
  const removeRow = async (r: SheetGridRow) => {
    const name = cellStr(r.cells[primaryId ?? -1]) || 'this row';
    const kids = countDesc(r.id);
    const msg = kids > 0 ? `Delete "${name}" and its ${kids} row(s) below it?` : `Delete "${name}"?`;
    if (await confirm({ title: 'Delete row', message: msg, confirmLabel: 'Delete', danger: true })) delRow.mutate({ rowId: r.id });
  };

  // read-only display for a symbol / colour / plain cell.
  const renderView = (c: SheetGridColumn, r: SheetGridRow): ReactNode => {
    const v = r.cells[c.id];
    if (isSymbolCol(c)) {
      const dot = DOT[String(v ?? '').trim().toLowerCase()];
      if (dot) return <span className="grp-dot" style={{ background: dot }} title={String(v)}><span className="sr-only">{String(v)}</span></span>;
      return cellStr(v) || null;
    }
    if (isColorCol(c)) {
      const s = String(v ?? '').trim();
      if (!s) return null;
      const sw = SWATCH[s.toLowerCase()];
      return <span className="grp-colorcell">{sw && <span className="grp-swatch" style={{ background: sw }} />}{s}</span>;
    }
    if (c.type === 'DATE' || c.type === 'DATETIME') return df.cell(c.type, v) || null; // in the user's date format
    return cellStr(v) || null;
  };

  // the inner content of a non-primary cell (editable when the user has grp:write).
  // a dropdown opens its list on the click that picked the cell, as in Smartsheet (not a second click)
  const openList = (el: HTMLSelectElement | null) => {
    try { (el as (HTMLSelectElement & { showPicker?: () => void }) | null)?.showPicker?.(); } catch { /* no picker API: it's focused */ }
  };
  const cellContent = (c: SheetGridColumn, r: SheetGridRow): ReactNode => {
    const v = r.cells[c.id];
    if (c.type === 'CHECKBOX') {
      if (canWrite) {
        const box = <input type="checkbox" className="grp-cb" aria-label={`${c.title}${rowName(r) ? `, ${rowName(r)}` : ''}`} checked={v === true} onClick={stop} onChange={(e) => writeCell(r.id, c.id, e.currentTarget.checked)} />;
        // touch: the label fills the cell, so a tap anywhere in it ticks the box
        return coarse ? <label className="grp-cb-hit" onClick={stop}>{box}</label> : box;
      }
      return v === true ? <span className="grp-check"><IconCheck size={13} /></span> : null;
    }
    if (c.type === 'MULTI_PICKLIST') {
      const parts = String(v ?? '').split(',').map((s) => s.trim()).filter(Boolean);
      if (canWrite) return <MultiPick label={c.title} value={parts} options={c.options} onChange={(next) => writeCell(r.id, c.id, next)} />;
      if (!parts.length) return null;
      return <span className="grp-chips">{parts.map((p, i) => <span key={i} className="grp-chip">{p}</span>)}</span>;
    }
    // touch: a status / picklist cell is its own native picker. The <select> sits invisibly over the dot
    // and fills the cell, so one tap opens the OS list (an autofocused select doesn't open on iOS).
    if (canWrite && coarse && (isSymbolCol(c) || c.type === 'PICKLIST') && c.options.length) {
      const cur = cellStr(v);
      return (
        <span className="grp-pick">
          {renderView(c, r) ?? <span className="grp-empty" />}
          <select
            className="grp-pick-sel" value={cur} onClick={stop}
            aria-label={`${c.title}${rowName(r) ? `, ${rowName(r)}` : ''}`}
            onChange={(e) => { const val = e.currentTarget.value; writeCell(r.id, c.id, val === '' ? null : val); }}
          >
            <option value="" />
            {cur && !c.options.includes(cur) && <option value={cur}>{cur}</option>}
            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </span>
      );
    }
    if (canWrite && editingHere(r, c)) {
      if ((isSymbolCol(c) || c.type === 'PICKLIST') && c.options.length) {
        return (
          <select
            className="grp-edit" autoFocus value={draft} onClick={stop} aria-label={c.title} ref={openList}
            onChange={(e) => { const val = e.currentTarget.value; writeCell(r.id, c.id, val === '' ? null : val); setEditing(null); }}
            onBlur={() => setEditing(null)}
          >
            <option value="" />
            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      const isDate = c.type === 'DATE' || c.type === 'DATETIME';
      return (
        <input
          className="grp-edit" autoFocus type={isDate ? 'date' : 'text'} value={draft} onClick={stop} aria-label={c.title}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={() => commitText(r, c)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitText(r, c); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }}
        />
      );
    }
    const vw = renderView(c, r);
    if (canWrite) {
      return (
        <span
          className="grp-editable" role="button" tabIndex={0} aria-label={vw == null ? `Edit ${c.title}` : undefined}
          onClick={(e) => beginEdit(r, c, e)} onKeyDown={editKey(r, c)} title="Click to edit"
        >{vw ?? <span className="grp-empty" />}</span>
      );
    }
    return vw;
  };

  // the boat / row name — an editable field for editors (mouse), plain text otherwise. On touch it is
  // wrapping text, so a tap on a card header opens the card; the ✎ beside it renames (NameField).
  const nameField = (r: SheetGridRow, cls: string, textCls: string, placeholder: string): ReactNode => {
    if (primaryId == null) return null;
    return (
      <NameField
        value={cellStr(r.cells[primaryId])}
        canWrite={canWrite}
        className={cls}
        textClassName={textCls}
        placeholder={placeholder}
        readOnly="text"
        rename
        onCommit={(v) => writeCell(r.id, primaryId, v || null)}
      />
    );
  };

  const lastSync = dataUpdatedAt ? df.time(dataUpdatedAt) : null;
  const hiddenCount = columns.length - cols.length;

  // one-click reset for every filter, mirroring Service/Overview/Schedule.
  const activeFilters =
    (q.trim() ? 1 : 0) + (statusSel !== 'ALL' ? 1 : 0) + (onlyOpen ? 1 : 0) + (monthSel !== 'ALL' ? 1 : 0) + (modelSel !== 'ALL' ? 1 : 0);
  const clearFilters = () => { setQ(''); setStatusSel('ALL'); setOnlyOpen(false); setMonthSel('ALL'); setModelSel('ALL'); };
  // shared by both views: boats exist but the filters hide them all, vs. a sheet with no boats yet.
  const emptyState = topLevel.length > 0
    ? <EmptyState title="No boats match" hint="Try adjusting or clearing the filters above." />
    : <EmptyState title="No boats on this sheet yet" hint={canWrite ? 'Use “+ Boat” to add one.' : undefined} />;

  // ── board: one card per boat ─────────────────────────────────────────────
  // the first status (symbol) column leads a Board item on phones: dot · name · actions on one line
  const leadSymId = symbolCols.find((c) => valueCols.some((v) => v.id === c.id))?.id;
  // phones list an item's fields under it; these add nothing there, so they are left out (the Sheet view
  // still has every cell): the boat's own SAP / model / hull colour and its dates repeated on every item,
  // and empty cells for viewers and on section rows (an editor fills an inspection point's empty fields)
  const isBoatLevel = (c: SheetGridColumn) => /^sap\b|model|colou?r/i.test(c.title.trim()) || dateCols.some((d) => d.id === c.id);
  const skipOnPhone = (c: SheetGridColumn, r: SheetGridRow, boat: SheetGridRow) => {
    const val = c.type === 'CHECKBOX' ? (r.cells[c.id] === true ? '✓' : '') : cellStr(r.cells[c.id]).trim();
    if (c.id === leadSymId) return !val && hasKids(r.id);
    if (isBoatLevel(c) && (!val || val === cellStr(boat.cells[c.id]).trim())) return true;
    return !val && (!canWrite || hasKids(r.id));
  };

  const boardRow = (r: SheetGridRow, depth: number, boat: SheetGridRow): ReactElement => {
    const tint = r.fmt ? (r.fmt[String(primaryId)] ?? Object.values(r.fmt)[0]) : undefined;
    const parent = hasKids(r.id);
    return (
      <tr
        key={r.id}
        className={`svc2-trow${tint ? ' grp-tinted' : ''}`}
        style={{ '--depth': depth, ...(tint ? { background: tint, color: '#1a1a1a' } : null) } as CSSProperties}
      >
        <td className="svc2-tname-td">
          <div className="svc2-tname">
            {nameField(r, `svc2-tin${parent ? ' grp-name-group' : ''}`, `svc2-tname-txt${parent ? ' grp-name-group' : ''}`, parent ? 'Section…' : 'Item…')}
          </div>
        </td>
        {valueCols.map((c) => (
          <td
            key={c.id}
            className={`svc2-tcell${c.id === leadSymId ? ' grp-sym' : ''}${skipOnPhone(c, r, boat) ? ' grp-skip' : ''}`}
            data-label={c.title}
            onPointerEnter={histEnter(r.id, c.id, c.title, rowName(r))}
            onPointerLeave={histLeave}
            {...histPress(r.id, c.id, c.title, rowName(r))}
          >
            {cellContent(c, r)}
            {histButton(r.id, c.id, c.title, rowName(r))}
          </td>
        ))}
        {canWrite && (
          <td className="svc2-tact-td">
            <button className="svc2-iact" title="Add a row inside this one" aria-label={`Add a row inside ${rowName(r) || 'this row'}`} disabled={addRow.isPending} onClick={() => addChild(r)}>＋</button>
            <button className="svc2-iact del" title="Delete this row" aria-label={`Delete ${rowName(r) || 'this row'}`} disabled={delRow.isPending} onClick={() => void removeRow(r)}><IconTrash size={13} /></button>
          </td>
        )}
      </tr>
    );
  };

  const boatCard = (b: SheetGridRow): ReactElement => {
    const info = boatInfo.get(b.id) ?? { green: 0, total: 0, statuses: new Set<string>() };
    const open = isBoatOpen(b.id);
    const swatchCol = cols.find((c) => isColorCol(c) && cellStr(b.cells[c.id]).trim());
    const colorVal = swatchCol ? cellStr(b.cells[swatchCol.id]).trim() : '';
    const sw = colorVal ? SWATCH[colorVal.toLowerCase()] : undefined;
    const attention = info.total > 0 && info.green < info.total;
    const rows = open ? boatDescendants(b.id) : [];
    const boatName = cellStr(b.cells[primaryId ?? -1]) || 'boat';
    const sapVal = sapCol ? cellStr(b.cells[sapCol.id]).trim() : '';
    // the SAP, then the hull colour, beside the name
    const sub = [sapVal && `SAP ${sapVal}`, colorVal].filter(Boolean).join(' · ');
    return (
      <div key={b.id} data-row-id={b.id} className={`svc2-card ${open ? 'open' : ''} ${attention ? 'attention' : ''}`}>
        <div className="svc2-vhd" onClick={() => toggleBoat(b.id)}>
          <button
            type="button"
            className={`svc2-vchev${open ? ' is-open' : ''}`}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${boatName}`}
            onClick={(e) => { e.stopPropagation(); toggleBoat(b.id); }}
          ><IconChevronRight size={14} /></button>
          {sw && <span className="grp-swatch" style={{ background: sw }} title={colorVal} />}
          {nameField(b, 'svc2-vname-in', 'svc2-vname', 'Boat name…')}
          {sub && <span className="svc2-vsub">{sub}</span>}
          {attention && <Pill kind="warn" title="Not all inspection points are green">needs attention</Pill>}
          {info.total > 0 ? (
            <span className={`svc2-badge ${info.green === info.total ? 'done' : ''}`} title="Inspection points green / total">{info.green}/{info.total}</span>
          ) : (
            <span className="svc2-badge">{countDesc(b.id)} row{countDesc(b.id) === 1 ? '' : 's'}</span>
          )}
        </div>
        {open && (
          <div className="svc2-vbd">
            {rows.length ? (
              <div className="tbl-wrap svc2-tasks">
                <table className="svc2-ttable">
                  <thead>
                    <tr>
                      <th className="svc2-tname-th">Item</th>
                      {valueCols.map((c) => <th key={c.id} className={isSymbolCol(c) ? 'svc2-th-pri' : ''} title={c.title}>{c.title}{c.symbol ? ' •' : ''}</th>)}
                      {canWrite && <th className="svc2-tact-th" aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>{rows.map(({ r, depth }) => boardRow(r, depth, b))}</tbody>
                </table>
              </div>
            ) : (
              <div className="muted" style={{ padding: '4px 2px' }}>No inspection rows{query ? ' match the search' : ''}.</div>
            )}
            {canWrite && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button className="svc2-add" disabled={addRow.isPending} onClick={() => addChild(b)}>+ Add row</button>
                <button className="svc2-add" onClick={() => applyTo(b.id)} title="Add the QA rows this boat is missing, from another boat"><IconCopy size={13} /> QA rows from another boat</button>
                <button className="svc2-iact del" title="Delete boat" disabled={delRow.isPending} onClick={() => void removeRow(b)}><IconTrash size={13} /> delete boat</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHeader title="GRP Inspection">
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[{ value: 'sheet', label: 'Sheet' }, { value: 'board', label: 'Board' }]}
        />
        {view === 'sheet' && (
          <button className="btn btn-sm" onClick={everExpanded ? collapseAll : expandAll}>
            {everExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
        {view === 'sheet' && (hiddenCount > 0 || showEmpty) && (
          <button className="btn btn-sm" onClick={() => setShowEmpty((s) => !s)} title="Blank columns are hidden to reduce scrolling">
            {showEmpty ? 'Hide empty columns' : `Show all columns${hiddenCount ? ` (+${hiddenCount})` : ''}`}
          </button>
        )}
        {canWrite && <button className="btn btn-sm" onClick={() => applyTo(null)} title="Give a boat on the sheet the QA rows it's missing, from another boat"><IconCopy size={14} /> Apply QA rows</button>}
        {canWrite && <button className="btn btn-sm btn-primary" onClick={addBoat} disabled={addRow.isPending}>+ Boat</button>}
        {lastSync && <span className="muted">Synced {lastSync}</span>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} /></button>
      </PageHeader>

      {isLoading && <LoadingState label="Loading GRP sheet…" />}
      {error && !data && <ErrorState error={error} onRetry={() => void refetch()} />}
      {error && data && <InlineError error={error} />}
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
      {addRow.isError && <InlineError error={addRow.error} onDismiss={() => addRow.reset()} />}
      {delRow.isError && <InlineError error={delRow.error} onDismiss={() => delRow.reset()} />}

      {data && topLevel.length > 0 && (
        <div className="svc-kpis">
          <span className="svc-kpi"><b>{kpis.boats}</b> boat{kpis.boats === 1 ? '' : 's'}</span>
          <span className="svc-kpi"><b>{kpis.allGreen}</b> all green</span>
          {kpis.total > 0 && <span className="svc-kpi"><b>{kpis.green}/{kpis.total}</b> points green</span>}
          <span style={{ flex: 1 }} />
          {view === 'sheet' && <span className="muted">{visible.length} row{visible.length === 1 ? '' : 's'}</span>}
        </div>
      )}

      {data && topLevel.length > 0 && (
        <FilterBar activeCount={activeFilters} onClear={clearFilters} search={<SearchInput value={q} onChange={setQ} placeholder="Search the sheet" width={210} />}>
          {allStatuses.length > 0 && (
            <ChipGroup label="Status">
              <Chip active={statusSel === 'ALL'} onClick={() => setStatusSel('ALL')}>All</Chip>
              {allStatuses.map((s) => (
                <Chip key={s} active={statusSel === s} dot={DOT[s] ?? '#adb5bd'} onClick={() => setStatusSel((p) => (p === s ? 'ALL' : s))}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Chip>
              ))}
            </ChipGroup>
          )}
          <FilterToggle checked={onlyOpen} onChange={setOnlyOpen} title="Only boats with an inspection point that isn't green">needs attention</FilterToggle>
          {models.length > 1 && (
            <FilterSelect
              label="Model"
              value={modelSel}
              onChange={setModelSel}
              options={[{ value: 'ALL', label: 'All models' }, ...models.map((m) => ({ value: m.key, label: `${m.label} · ${m.n}` }))]}
              ariaLabel="Model"
            />
          )}
          {dateCols.length > 1 && (
            <FilterSelect
              label="Date"
              value={String(dateColId ?? '')}
              onChange={(v) => setDateColId(Number(v))}
              // the default column leads: FilterSelect reads options[0] as the neutral (untinted) value
              options={[...dateCols].sort((a, b) => Number(b.id === defaultDateId) - Number(a.id === defaultDateId)).map((c) => ({ value: String(c.id), label: c.title }))}
              ariaLabel="Date column for the Month filter"
            />
          )}
          {dateCols.length > 0 && (
            <FilterSelect
              label="Month"
              value={monthSel}
              onChange={setMonthSel}
              options={[{ value: 'ALL', label: 'All months' }, ...months.map((m) => ({ value: m, label: monthLabel(m) }))]}
              ariaLabel="Month"
            />
          )}
          <span className="grp-sortpair">
            <FilterSelect
              label="Sort"
              value={sort ? String(sort.key) : ''}
              onChange={(v) => setSort(v ? { key: Number(v), dir: sort?.dir ?? 'asc' } : null)}
              options={[{ value: '', label: 'Sheet order' }, ...sortOptions]}
              ariaLabel="Sort the boats by"
            />
            {sort && (
              <button
                type="button"
                className="btn btn-sm grp-sortdir"
                onClick={() => setSort({ key: sort.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
                aria-label={sort.dir === 'asc' ? 'Sorted ascending: switch to descending' : 'Sorted descending: switch to ascending'}
                title={sort.dir === 'asc' ? 'Ascending (A to Z, oldest first). Click for descending.' : 'Descending (Z to A, newest first). Click for ascending.'}
              >
                <SortMark dir={sort.dir} />
              </button>
            )}
          </span>
        </FilterBar>
      )}


      {/* ── BOARD ── */}
      {data && view === 'board' && (
        <div className="svc-acc grp-board">
          {visibleBoats.map((b) => boatCard(b))}
          {visibleBoats.length === 0 && emptyState}
        </div>
      )}

      {/* ── SHEET ── */}
      {data && view === 'sheet' && (
        <div className="tbl-wrap is-grid grid-fill">
          <table className="tbl grp-tbl grp-sheet">
            <thead>
              <tr>
                {sheetCols.map((c) => (
                  <th key={c.id} {...sortTh(c.id, c.id === primaryId ? 'grp-name-col' : undefined)}>{c.title}<SortMark dir={dirOf(c.id)} /></th>
                ))}
                {canWrite && <th className="grp-actions-col" aria-label="Row actions" />}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => {
                const depth = depthOf(r);
                const canToggle = hasKids(r.id);
                const isCollapsed = canToggle && !searchKeep && !expanded.has(r.id);
                const tint = r.fmt ? (r.fmt[String(primaryId)] ?? Object.values(r.fmt)[0]) : undefined;
                const tdStyle = tint ? { background: tint, color: '#1a1a1a' } : undefined;
                const rowClass = [depth === 0 ? 'grp-row-top' : '', canToggle ? 'grp-row-parent' : '', tint ? 'grp-tinted' : ''].filter(Boolean).join(' ');
                const editingName = canWrite && editing?.rowId === r.id && editing?.colId === primaryId;
                let badge: ReactElement | null = null;
                if (isCollapsed) {
                  const ru = rollup(r.id);
                  if (ru.total > 0) badge = <span className={`grp-rollup${ru.green === ru.total ? ' done' : ''}`} title={`${ru.green} of ${ru.total} inspection points green`}>{ru.green}/{ru.total}</span>;
                  else { const n = countDesc(r.id); if (n > 0) badge = <span className="grp-count" title={`${n} row${n === 1 ? '' : 's'}`}>{n}</span>; }
                }
                return (
                  <tr
                    key={r.id}
                    data-row-id={r.id}
                    className={rowClass}
                    style={tint ? { background: tint } : undefined}
                    // mouse convenience only — the twist button below is the keyboard / screen-reader control,
                    // so the row keeps its table semantics and its inner controls stay reachable
                    onClick={canToggle ? () => toggle(r.id) : undefined}
                  >
                    {sheetCols.map((c) => {
                      if (c.id === primaryId) {
                        return (
                          <td
                            key={c.id}
                            className="grp-name-col"
                            style={{ ...tdStyle, paddingLeft: isPhone ? 4 + depth * 10 : 8 + depth * 18 }}
                            // a right-click anywhere on the name renames it (touch has the ✎ and its own long-press)
                            onContextMenu={canWrite && !coarse && !editingName ? (e) => { e.preventDefault(); beginEdit(r, c, e); } : undefined}
                          >
                            <span className="grp-name-inner">
                              {canToggle ? (
                                <button
                                  type="button"
                                  className="grp-twist"
                                  aria-expanded={!isCollapsed}
                                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${rowName(r) || 'row'}`}
                                  onClick={(e) => { e.stopPropagation(); toggle(r.id); }}
                                >{isCollapsed ? '+' : '−'}</button>
                              ) : <span className="grp-twist-spacer" />}
                              {coarse ? (
                                // touch: the name is text, so a tap on a parent row toggles it (the row's
                                // click); renaming goes through the ✎ beside it
                                nameField(r, 'grp-edit', `pin-clamp${depth === 0 ? ' grp-name-boat' : depth === 1 ? ' grp-name-group' : ''}`, 'Name…')
                              ) : editingName ? (
                                <input
                                  className="grp-edit" autoFocus value={draft} onClick={stop} aria-label={c.title}
                                  onChange={(e) => setDraft(e.currentTarget.value)}
                                  onBlur={() => commitText(r, c)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitText(r, c); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }}
                                />
                              ) : (
                                <span
                                  className={`${depth === 0 ? 'grp-name-boat' : depth === 1 ? 'grp-name-group' : ''}${canWrite ? ' grp-editable' : ''}`}
                                  onClick={canWrite ? (e) => beginEdit(r, c, e) : undefined}
                                  {...(canWrite
                                    ? { role: 'button' as const, tabIndex: 0, onKeyDown: editKey(r, c), 'aria-label': cellStr(r.cells[c.id]) ? undefined : `Edit ${c.title}` }
                                    : {})}
                                >
                                  {cellStr(r.cells[c.id]) || (canWrite ? <span className="grp-empty" /> : '')}
                                </span>
                              )}
                              {badge}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={c.id}
                          className={c.type === 'CHECKBOX' || isSymbolCol(c) ? 'grp-c' : undefined}
                          style={tdStyle}
                          onPointerEnter={histEnter(r.id, c.id, c.title, rowName(r))}
                          onPointerLeave={histLeave}
                          {...histPress(r.id, c.id, c.title, rowName(r))}
                        >
                          {cellContent(c, r)}
                        </td>
                      );
                    })}
                    {canWrite && (
                      <td className="grp-actions" style={tdStyle}>
                        <button className="grp-rowbtn" title="Add a row inside this one" aria-label={`Add a row inside ${rowName(r) || 'this row'}`} disabled={addRow.isPending} onClick={(e) => { e.stopPropagation(); addChild(r); }}>＋</button>
                        {r.parentId == null && (
                          <button className="grp-rowbtn" title="Add the QA rows this boat is missing, from another boat" aria-label={`Apply QA rows from another boat to ${rowName(r) || 'this boat'}`} onClick={(e) => { e.stopPropagation(); applyTo(r.id); }}><IconCopy size={13} /></button>
                        )}
                        <button className="grp-rowbtn grp-rowbtn-del" title="Delete this row" aria-label={`Delete ${rowName(r) || 'this row'}`} disabled={delRow.isPending} onClick={(e) => { e.stopPropagation(); void removeRow(r); }}><IconTrash size={13} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {visible.length > shownRows.length && (
                <tr className="grp-more">
                  <td colSpan={(sheetCols.length || 1) + (canWrite ? 1 : 0)}>
                    <div className="grp-more-in">
                      <button type="button" className="btn btn-sm" onClick={() => setRowLimit((n) => n + rowPage)}>
                        Show {Math.min(rowPage, visible.length - shownRows.length)} more rows
                      </button>
                      <span className="muted">{shownRows.length} of {visible.length} shown</span>
                    </div>
                  </td>
                </tr>
              )}
              {visible.length === 0 && (
                <tr><td colSpan={(cols.length || 1) + (canWrite ? 1 : 0)}>{emptyState}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {histPopover}
      {confirmNode}
      {adding && primaryId != null && (
        <GrpAddBoat columns={columns} rows={allRows} primaryId={primaryId} onClose={() => setAdding(false)} onAdded={onBoatAdded} />
      )}
      {applying && primaryId != null && (
        <GrpApplyTasks rows={allRows} primaryId={primaryId} boatId={applying.boatId} onClose={() => setApplying(null)} onApplied={onBoatAdded} />
      )}
    </>
  );
}
